import { memo, useMemo, useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Fieldset } from 'primereact/fieldset'
import { buildInsights, buildRevisionsObservations } from '../../utils/insights'
import type { ActiveFilters } from '../../types/Stats'
import { GEO_COLORS } from '../../types/Stats'
import { useLocalizationLabels } from '../../context/LocalizationLabelContext'
import { useStatsRevisions } from '../../hooks/useStatsRevisions'
import type { Insights } from '../../api'
import './StatsChartInsights.css'

interface Props {
  geoOrder:       string[]
  insights:       Insights | null
  yearRange:      [number, number]
  metric:         string | null
  unit:           string
  scope:          string
  geo:            string
  appliedFilters: ActiveFilters
}

const COMPARISON_TAB = '__comparison__'
const REVISIONS_TAB  = '__revisions__'

const StatsChartInsights = memo(function StatsChartInsights({
  metric, geoOrder, insights, unit, yearRange, appliedFilters, scope, geo,
}: Props) {
  const { t } = useTranslation('mainStats')
  const { geoNameOptionLabels, statsLabels } = useLocalizationLabels()

  const unitLabel = unit ? (statsLabels.units?.[unit]?.unit ?? unit) : ''
  const metricLabel = metric ? (statsLabels.metrics?.[metric]?.name ?? metric) : ''

  // Insights pozorovania
  const insightsData = useMemo(() => {
    if (!insights || geoOrder.length === 0) return null
    return buildInsights(metricLabel, insights, geoOrder, geoNameOptionLabels, yearRange, unitLabel, t)
  }, [insights, geoOrder, geoNameOptionLabels, yearRange, unitLabel, t, metricLabel])

  // Revisions
  const { revisionsData, revisionsError } = useStatsRevisions(scope, geo, metric, appliedFilters)
  const revisionsObservations = useMemo(
    () => buildRevisionsObservations(revisionsData, revisionsError, unitLabel),
    [revisionsData, revisionsError, unitLabel],
  )

  // Tabs
  const [activeTabId, setActiveTabId] = useState<string>('')
  const geoKeysStr = useMemo(() => geoOrder.join(','), [geoOrder])

  useEffect(() => { setActiveTabId(geoOrder[0] ?? '') }, [geoKeysStr])
  if (!insightsData) return null
  const hasComparison = insightsData.comparison.length > 0

  let activeObservations: typeof insightsData.comparison = []
  let activeHeader: typeof insightsData.comparisonHeader = null

  if (activeTabId === COMPARISON_TAB) {
    activeObservations = insightsData.comparison
    activeHeader = insightsData.comparisonHeader
  } else if (activeTabId === REVISIONS_TAB) {
    activeObservations = revisionsObservations
    activeHeader = null  
  } else {
    activeObservations = insightsData.perGeo[activeTabId] ?? []
    activeHeader = insightsData.perGeoHeaders[activeTabId] ?? null
  }

  const showRevisionsDescription = activeTabId === REVISIONS_TAB

  return (
    <Fieldset legend={t('insights.legend')} className="chart-insights">
      <div className="insights-tabs">
        {geoOrder.map((geoKey, i) => {
          const label = geoNameOptionLabels[geoKey]?.label ?? geoKey
          const active = activeTabId === geoKey
          return (
            <button
              key={geoKey}
              className={`insights-tab${active ? ' active' : ''}`}
              style={{
                borderBottomColor: active ? GEO_COLORS[i] : 'transparent',
                color:             active ? GEO_COLORS[i] : undefined,
              }}
              onClick={() => setActiveTabId(geoKey)}
            >
              {label}
            </button>
          )
        })}
        {hasComparison && (
          <button
            className={`insights-tab${activeTabId === COMPARISON_TAB ? ' active' : ''}`}
            style={{
              borderBottomColor:
                activeTabId === COMPARISON_TAB
                  ? 'var(--text-color-secondary, #6c757d)'
                  : 'transparent'
            }}
            onClick={() => setActiveTabId(COMPARISON_TAB)}
          >
            {t('insights.comparisonTab')}
          </button>
        )}
        <button
          className={`insights-tab${activeTabId === REVISIONS_TAB ? ' active' : ''}`}
          style={{
            borderBottomColor:
              activeTabId === REVISIONS_TAB
                ? 'var(--text-color-secondary, #6c757d)'
                : 'transparent'
          }}
          onClick={() => setActiveTabId(REVISIONS_TAB)}
        >
          {t('revisions.tab')}
        </button>
      </div>
      {/* Header — pre per-geo a comparison taby */}
      {activeHeader && (
        <p className="insights-header">
          <Trans
            ns="mainStats"
            i18nKey={activeHeader.i18nKey}
            values={activeHeader.values}
            components={activeHeader.components}
          />
        </p>
      )}
      {/* Description — pre revisions tab */}
      {showRevisionsDescription && (
        <p className="insights-header">{t('revisions.description')}</p>
      )}
      {/* Fallback pre prípad že geo nemá pozorovania */}
      {activeObservations.length === 0 && !showRevisionsDescription && (
        <p className="insights-warning">{t('insights.noObservations')}</p>
      )}
      <ul className="insights-list">
        {activeObservations.map((obs, i) => (
          <li key={i} className="insights-item insights-info">
            <Trans
              ns="mainStats"
              i18nKey={obs.i18nKey}
              values={obs.values}
              components={obs.components}
            />
          </li>
        ))}
      </ul>
    </Fieldset>
  )
})

export { StatsChartInsights }